/**
 * Codes pays (ISO 3166-1 alpha-2), et rien de plus.
 *
 * Aucun nom n'est stocké ici, dans aucune langue. `Intl.DisplayNames` sait déjà traduire
 * un code en nom de pays, dans la langue qu'on lui donne — le vérifier une fois a suffi
 * (voir `countries.test.ts`). Une table `code → nom français` aurait dû être retraduite
 * pour chaque langue de l'internationalisation, recopiée quatre ou cinq fois avec le
 * risque d'y semer des variantes ; ce fichier ne bouge presque jamais, et la traduction
 * ne coûte donc rien à demander à chaque affichage plutôt qu'à figer.
 *
 * La liste elle-même vient de `mledoze/countries` (jeu de données ISO 3166-1 tenu à jour
 * publiquement) — 250 territoires, y compris les dépendances qui ont leur propre code
 * (Aruba, Hong Kong…), parce qu'un parent peut résider dans l'un comme dans l'autre.
 */
export const COUNTRY_CODES = [
    'AD', 'AE', 'AF', 'AG', 'AI', 'AL', 'AM', 'AO', 'AQ', 'AR', 'AS', 'AT',
    'AU', 'AW', 'AX', 'AZ', 'BA', 'BB', 'BD', 'BE', 'BF', 'BG', 'BH', 'BI',
    'BJ', 'BL', 'BM', 'BN', 'BO', 'BQ', 'BR', 'BS', 'BT', 'BV', 'BW', 'BY',
    'BZ', 'CA', 'CC', 'CD', 'CF', 'CG', 'CH', 'CI', 'CK', 'CL', 'CM', 'CN',
    'CO', 'CR', 'CU', 'CV', 'CW', 'CX', 'CY', 'CZ', 'DE', 'DJ', 'DK', 'DM',
    'DO', 'DZ', 'EC', 'EE', 'EG', 'EH', 'ER', 'ES', 'ET', 'FI', 'FJ', 'FK',
    'FM', 'FO', 'FR', 'GA', 'GB', 'GD', 'GE', 'GF', 'GG', 'GH', 'GI', 'GL',
    'GM', 'GN', 'GP', 'GQ', 'GR', 'GS', 'GT', 'GU', 'GW', 'GY', 'HK', 'HM',
    'HN', 'HR', 'HT', 'HU', 'ID', 'IE', 'IL', 'IM', 'IN', 'IO', 'IQ', 'IR',
    'IS', 'IT', 'JE', 'JM', 'JO', 'JP', 'KE', 'KG', 'KH', 'KI', 'KM', 'KN',
    'KP', 'KR', 'KW', 'KY', 'KZ', 'LA', 'LB', 'LC', 'LI', 'LK', 'LR', 'LS',
    'LT', 'LU', 'LV', 'LY', 'MA', 'MC', 'MD', 'ME', 'MF', 'MG', 'MH', 'MK',
    'ML', 'MM', 'MN', 'MO', 'MP', 'MQ', 'MR', 'MS', 'MT', 'MU', 'MV', 'MW',
    'MX', 'MY', 'MZ', 'NA', 'NC', 'NE', 'NF', 'NG', 'NI', 'NL', 'NO', 'NP',
    'NR', 'NU', 'NZ', 'OM', 'PA', 'PE', 'PF', 'PG', 'PH', 'PK', 'PL', 'PM',
    'PN', 'PR', 'PS', 'PT', 'PW', 'PY', 'QA', 'RE', 'RO', 'RS', 'RU', 'RW',
    'SA', 'SB', 'SC', 'SD', 'SE', 'SG', 'SH', 'SI', 'SJ', 'SK', 'SL', 'SM',
    'SN', 'SO', 'SR', 'SS', 'ST', 'SV', 'SX', 'SY', 'SZ', 'TC', 'TD', 'TF',
    'TG', 'TH', 'TJ', 'TK', 'TL', 'TM', 'TN', 'TO', 'TR', 'TT', 'TV', 'TW',
    'TZ', 'UA', 'UG', 'US', 'UY', 'UZ', 'VA', 'VC', 'VE', 'VG', 'VI', 'VN',
    'VU', 'WF', 'WS', 'YE', 'YT', 'ZA', 'ZM', 'ZW',
];
export function isCountryCode(value) {
    return COUNTRY_CODES.includes(value);
}
/**
 * Nom du pays dans la langue demandée, via `Intl.DisplayNames`.
 *
 * `locale` suit le code de langue de l'internationalisation du produit (`fr`, `en`…), pas
 * une locale complète : `Intl.DisplayNames` sait résoudre `'en'` seul aussi bien que
 * `'en-US'`, et le produit ne distingue pas les variantes régionales d'une langue.
 */
export function countryDisplayName(code, locale) {
    return new Intl.DisplayNames([locale, 'fr'], { type: 'region' }).of(code) ?? code;
}
/**
 * Les pays proposés en tête de liste, avant le reste par ordre alphabétique.
 *
 * La France d'abord — c'est le pays de la quasi-totalité des dossiers — puis ses sept
 * voisins terrestres et l'Andorre et Monaco, dont les enclaves : une famille séparée vit
 * plus souvent entre la France et l'un de ces pays qu'entre la France et l'Uruguay, et
 * l'autocomplétion doit répondre vite au cas fréquent avant de céder au cas rare.
 */
export const COUNTRIES_MIS_EN_AVANT = [
    'FR', 'BE', 'CH', 'DE', 'IT', 'ES', 'LU', 'MC', 'AD', 'GB',
];
/**
 * Pays proposés pour une saisie, dans la langue demandée.
 *
 * Champ vide : les pays mis en avant, dans leur ordre — pas de tri alphabétique qui
 * renverrait la France au milieu de la liste. Champ rempli : tous les pays dont le nom
 * commence par ce qui est tapé, dans la langue affichée ; « fra » trouve « France » et
 * « al » trouve « Albanie » et « Algérie ». Recherche insensible aux accents et à la
 * casse — un clavier français tape « d » avant de songer aux capitales.
 */
export function suggestCountries(query, locale) {
    const tous = COUNTRY_CODES.map((code) => ({ code, name: countryDisplayName(code, locale) }));
    const saisie = query.trim();
    if (!saisie) {
        return COUNTRIES_MIS_EN_AVANT.map((code) => ({ code, name: countryDisplayName(code, locale) }));
    }
    const normalise = normaliserPourRecherche(saisie);
    return tous
        .filter((pays) => normaliserPourRecherche(pays.name).startsWith(normalise))
        .sort((a, b) => a.name.localeCompare(b.name, locale));
}
/** Minuscules, sans accents : « Île-de-France » et « ile de france » doivent se rencontrer. */
export function normaliserPourRecherche(texte) {
    return texte
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase();
}
//# sourceMappingURL=countries.js.map